import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NetworkStatusService, NetworkStatus } from '../../services/network/network-status.service';

type BannerState = 'hidden' | 'offline' | 'back-online';

/**
 * Network status banner component that displays at the top of the screen
 * when the user goes offline and briefly shows when coming back online.
 *
 * Features:
 * - Slides down from top when offline
 * - Shows "Back online" message briefly when connection restored
 * - Modern glassmorphism design
 * - Auto-hides after coming back online
 */
@Component({
  selector: 'app-network-status-banner',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './network-status-banner.component.html',
  styleUrls: ['./network-status-banner.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideDown', [
      state('void', style({
        transform: 'translateY(-100%)',
        opacity: 0
      })),
      state('*', style({
        transform: 'translateY(0)',
        opacity: 1
      })),
      transition('void => *', animate('300ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
      transition('* => void', animate('250ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
  ]
})
export class NetworkStatusBannerComponent implements OnInit, OnDestroy {
  bannerState: BannerState = 'hidden';
  private destroy$ = new Subject<void>();
  private backOnlineTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private networkStatus: NetworkStatusService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Set initial state based on current network status
    if (!this.networkStatus.isOnline) {
      this.bannerState = 'offline';
    }

    // Subscribe to network status changes
    this.networkStatus.status$
      .pipe(takeUntil(this.destroy$))
      .subscribe((status: NetworkStatus) => {
        this.handleStatusChange(status);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearBackOnlineTimer();
  }

  /**
   * Handle network status changes
   */
  private handleStatusChange(status: NetworkStatus): void {
    this.clearBackOnlineTimer();

    if (!status.isOnline) {
      // User went offline
      this.bannerState = 'offline';
      this.cdr.markForCheck();
    } else if (status.wasOffline) {
      // User just came back online
      this.bannerState = 'back-online';
      this.cdr.markForCheck();

      // Auto-hide after 3 seconds
      this.backOnlineTimer = setTimeout(() => {
        this.bannerState = 'hidden';
        this.cdr.markForCheck();
      }, 3000);
    } else {
      // Initial state or already online
      this.bannerState = 'hidden';
      this.cdr.markForCheck();
    }
  }

  /**
   * Clear any pending back-online timer
   */
  private clearBackOnlineTimer(): void {
    if (this.backOnlineTimer) {
      clearTimeout(this.backOnlineTimer);
      this.backOnlineTimer = null;
    }
  }

  /**
   * Get whether the banner should be visible
   */
  get isVisible(): boolean {
    return this.bannerState !== 'hidden';
  }

  /**
   * Get whether we're in offline state
   */
  get isOffline(): boolean {
    return this.bannerState === 'offline';
  }

  /**
   * Get whether we're in back-online state
   */
  get isBackOnline(): boolean {
    return this.bannerState === 'back-online';
  }
}
